import apiClient from './client';
import type {
  UserSettings,
  User,
  Contact,
  ContactCreateResponse,
  Venue,
  Band,
  Concert,
  EmailTemplate,
  EmailImage,
  EmailImageUsage,
  MailingList,
  MailingListContact,
  Campaign,
  TaskStartedResponse,
  Communication,
  Reminder,
  ReminderCreate,
  TodoSearchParams,
  TodoCounts,
  CSVAnalyzeResponse,
  CSVValidateRequest,
  CSVValidateResponse,
  CSVImportRequest,
  ImportRecord,
  PaginatedResponse,
  InboxRow,
  VenueSearchParams,
  VenueNameMatch,
  VenueFilter,
  RecontactRequest,
  VenueUpdatePayload,
  RecipientsPreview,
  MemberAnswers,
  VenueView,
  VenueViewCreate,
  VenueViewFilters,
  ConcertSearchParams,
  ConcertView,
  ConcertViewCreate,
  ConcertViewFilters,
  DataCorrection,
  DataCorrectionCreate,
  DashboardAnalytics,
  AdminStats,
  AdminUser,
  AdminUserList,
  AdminCoreVenueList,
  AdminCoreVenue,
  AdminCoreVenueUpdate,
  MergeCoreVenueResponse,
  EmergencyRevokeResponse,
  BackgroundTask,
  BatchCancelResponse,
  BatchRequeueResponse,
  BatchSendNowResponse,
} from '../types';

// Auth endpoints
export const authAPI = {
  me: () => apiClient.get<User>('/auth/me'),
  login: () => apiClient.get<{ authorization_url: string; state: string }>('/auth/login?mode=auth', { withCredentials: true }),
  connectGmail: () => apiClient.get<{ authorization_url: string; state: string }>('/auth/login?mode=gmail', { withCredentials: true }),
  logout: () => apiClient.post('/auth/logout', {}, { withCredentials: true }),
  revokeGmail: () => apiClient.post('/auth/revoke-gmail'),
  deleteAccount: () => apiClient.delete('/auth/account', { withCredentials: true }),
  deleteAllData: () => apiClient.delete('/auth/data', { withCredentials: true }),
  deleteEmails: () => apiClient.delete('/auth/data/emails', { withCredentials: true }),
  deleteContacts: () => apiClient.delete('/auth/data/contacts', { withCredentials: true }),
  deleteVenuesAndContacts: () => apiClient.delete('/auth/data/venues-and-contacts', { withCredentials: true }),
  refresh: () =>
    apiClient.post<{ access_token: string; token_type: string }>('/auth/refresh', {}, { withCredentials: true }),
  // Partial patch: only the fields sent are written, the rest keep their stored value.
  updateSettings: (settings: Partial<UserSettings>) =>
    apiClient.patch<User>('/auth/settings', settings),
};

// Contacts endpoints
export const contactsAPI = {
  list: (params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<Contact>>('/contacts', { params }),

  search: (searchText: string, params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<Contact>>('/contacts/search', { params: { q: searchText, ...params } }),

  // summary: without communications, for callers that only need identity fields
  batchGet: (ids: string[], opts?: { summary?: boolean }) =>
    apiClient.post<Contact[]>('/contacts/batch', { ids, summary: opts?.summary ?? false }),

  inbox: (params?: { page?: number; page_size?: number; direction?: string; is_read?: boolean; q?: string }) =>
    apiClient.get<PaginatedResponse<InboxRow>>('/contacts/inbox', { params }),

  getById: (id: string) =>
    apiClient.get<Contact>(`/contacts/${id}`),

  /**
   * Create a contact — or get back the existing one holding that email (`reused: true`). With a
   * venue, the contact is linked to it and a `gatekeeper` flag becomes its role there.
   */
  create: (data: Partial<Contact>, venueId?: string) =>
    apiClient.post<ContactCreateResponse>('/contacts', data, {
      params: venueId ? { venue_id: venueId } : undefined,
    }),

  /**
   * Update a contact. With a venue, `flags` is chosen at that venue: `gatekeeper` sets the role
   * there and `none` lifts it; `expired` / `do_not_contact` apply everywhere. A 409
   * `email_in_use` means another contact holds the new email — merge them instead.
   */
  update: (id: string, data: Partial<Contact>, venueId?: string) =>
    apiClient.patch<Contact>(`/contacts/${id}`, data, {
      params: venueId ? { venue_id: venueId } : undefined,
    }),

  /** Merge contact `id` into `intoId`: `intoId` keeps both histories, links and roles; `id` goes. */
  merge: (id: string, intoId: string) =>
    apiClient.post<Contact>(`/contacts/${id}/merge`, { into: intoId }),

  delete: (id: string) =>
    apiClient.delete(`/contacts/${id}`),

  addCommunication: (id: string, communication: Communication) =>
    apiClient.post(`/contacts/${id}/communications`, communication),

  deleteCommunication: (id: string, commIndex: number, gmailMessageId?: string) =>
    apiClient.delete<Contact>(`/contacts/${id}/communications/${commIndex}`, {
      params: gmailMessageId ? { gmail_message_id: gmailMessageId } : undefined,
    }),

  markCommunicationRead: (contactId: string, commIndex: number) =>
    apiClient.patch(`/contacts/${contactId}/communications/${commIndex}/read`),

  markCommunicationUnread: (contactId: string, commIndex: number) =>
    apiClient.patch(`/contacts/${contactId}/communications/${commIndex}/unread`),

  markMessageRead: (contactId: string, commIndex: number, gmailMessageId: string) =>
    apiClient.patch(`/contacts/${contactId}/communications/${commIndex}/messages/${gmailMessageId}/read`),

  markMessageUnread: (contactId: string, commIndex: number, gmailMessageId: string) =>
    apiClient.patch(`/contacts/${contactId}/communications/${commIndex}/messages/${gmailMessageId}/unread`),

  markAllRead: (contactId: string) =>
    apiClient.patch(`/contacts/${contactId}/mark-all-read`),

  syncCommunications: (id: string) =>
    apiClient.post<{ success: boolean; total_threads: number; new_threads: number; updated_threads: number }>(`/contacts/${id}/sync`),
};

// Address filter value type
export interface AddressFilterValue {
  value: string;
  label: string;
  type: 'country' | 'region' | 'department';
}

// Venues endpoints
export const venuesAPI = {
  list: (params?: VenueSearchParams, signal?: AbortSignal) =>
    apiClient.get<PaginatedResponse<Venue>>('/venues', { params, signal }),

  // Deprecated: use list() with q param instead
  search: (searchText: string) =>
    apiClient.get<Venue[]>('/venues/search', { params: { search_text: searchText } }),

  /**
   * Name-only autocomplete (the add-venue modal's duplicate check). Cheap enough to run on every
   * keystroke, unlike list({ q }), which joins notes and contacts for the whole account.
   */
  nameLookup: (q: string, signal?: AbortSignal) =>
    apiClient.get<VenueNameMatch[]>('/venues/name-lookup', { params: { q }, signal }),

  getAddressFilterValues: () =>
    apiClient.get<AddressFilterValue[]>('/venues/address-values'),

  getStyleFilterValues: () =>
    apiClient.get<string[]>('/venues/style-values'),

  getIdealContactPeriodValues: () =>
    apiClient.get<string[]>('/venues/ideal-contact-period-values'),

  getConcertPeriodValues: () =>
    apiClient.get<string[]>('/venues/concert-period-values'),

  getSourceFilterValues: () =>
    apiClient.get<{ value: string; label: string }[]>('/venues/source-values'),

  getById: (id: string) =>
    apiClient.get<Venue>(`/venues/${id}`),

  create: (data: Partial<Venue>) =>
    apiClient.post<Venue>('/venues', data),

  update: (id: string, data: VenueUpdatePayload) =>
    apiClient.patch<Venue>(`/venues/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/venues/${id}`),

  /** Delete an explicit set of venues, or every venue matching a filter. */
  batchDelete: (selection: { ids: string[] } | { filter: VenueFilter }) =>
    apiClient.post<{ deleted: number; skipped: number }>('/venues/batch-delete', selection),

  /** Collect the mailing-list-eligible contacts of a venue selection, resolved server-side. */
  mailingListContacts: (
    selection: { venue_ids: string[] } | { filter: VenueFilter },
    options: { flag_option: 'active' | 'gatekeeper'; quantity: 'all' | 'one' },
  ) =>
    apiClient.post<MailingListContact[]>('/venues/mailing-list-contacts', { ...selection, ...options }),

  requestPublic: (id: string) =>
    apiClient.post<Venue>(`/venues/${id}/request-public`),

  addContact: (venueId: string, contactId: string) =>
    apiClient.post<Venue>(`/venues/${venueId}/contacts/${contactId}`),

  removeContact: (venueId: string, contactId: string) =>
    apiClient.delete<Venue>(`/venues/${venueId}/contacts/${contactId}`),

  /** Make a linked contact this venue's gatekeeper, or not — a role at this venue only. */
  setContactRole: (venueId: string, contactId: string, gatekeeper: boolean) =>
    apiClient.put<Venue>(`/venues/${venueId}/contacts/${contactId}/role`, { gatekeeper }),

  addTodo: (venueId: string, todo: ReminderCreate) =>
    apiClient.post<Reminder>(`/venues/${venueId}/todos`, todo),

  toggleFavorite: (venueId: string, bandId: string) =>
    apiClient.post<Venue>(`/venues/${venueId}/favorites/${bandId}`),

  /** Flag or unflag one venue. Sets the value rather than toggling, so a retry is harmless. */
  setFlag: (venueId: string, flagged: boolean) =>
    apiClient.put<void>(`/venues/${venueId}/flag`, { flagged }),

  /**
   * Mark one venue seen — the contact overlay calls this when it opens (docs/VENUE_SEEN.md).
   * Sets the value rather than toggling, so re-opening the overlay is harmless.
   */
  setSeen: (venueId: string, seen = true) =>
    apiClient.put<void>(`/venues/${venueId}/seen`, { seen }),

  /** Flag or unflag an explicit set of venues, or every venue matching a filter. */
  batchFlag: (selection: { ids: string[] } | { filter: VenueFilter }, flagged: boolean) =>
    apiClient.post<{ count: number }>('/venues/batch-flag', { ...selection, flagged }),

  /** Unflag every flagged venue, whatever the current filter. `count` is how many were flagged. */
  resetFlags: () =>
    apiClient.post<{ count: number }>('/venues/flags/reset'),

  /**
   * Pause an explicit set of venues, or every venue matching a filter, until a date the server
   * computes; `null` clears the pause. Pausing also forces the All-Bands interest to "Not For
   * Now" — the two are one state. See docs/VENUE_PAUSE.md.
   */
  batchRecontact: (selection: { ids: string[] } | { filter: VenueFilter }, recontact: RecontactRequest | null) =>
    apiClient.post<{ count: number }>('/venues/batch-recontact', { ...selection, recontact }),

  download: () =>
    apiClient.get<Blob>('/venues/download', { responseType: 'blob' }),
};

// Venue Views endpoints
export const venueViewsAPI = {
  list: () =>
    apiClient.get<VenueView[]>('/venue-views'),

  create: (data: VenueViewCreate) =>
    apiClient.post<VenueView>('/venue-views', data),

  update: (id: string, data: Partial<VenueViewCreate>) =>
    apiClient.patch<VenueView>(`/venue-views/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/venue-views/${id}`),

  reorder: (viewIds: string[]) =>
    apiClient.put('/venue-views/reorder', { view_ids: viewIds }),

  /** Overwrite the reserved "Latest" view, creating it on the first call. See docs/LATEST_VIEW.md. */
  saveLatest: (filters: VenueViewFilters) =>
    apiClient.put<VenueView>('/venue-views/latest', { filters }),
};

// Bands endpoints
export const bandsAPI = {
  list: (params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<Band>>('/bands', { params }),

  search: (searchText: string) =>
    apiClient.get<PaginatedResponse<Band>>('/bands/search', { params: { q: searchText } }),

  getById: (id: string) =>
    apiClient.get<Band>(`/bands/${id}`),

  create: (data: Partial<Band>) =>
    apiClient.post<Band>('/bands', data),

  update: (id: string, data: Partial<Band>) =>
    apiClient.patch<Band>(`/bands/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/bands/${id}`),

  /** Send every band id in the order wanted. See docs/BAND_ORDER.md. */
  reorder: (bandIds: string[]) =>
    apiClient.put('/bands/reorder', { band_ids: bandIds }),
};

// Concerts endpoints
export const concertsAPI = {
  list: (params?: ConcertSearchParams, signal?: AbortSignal) =>
    apiClient.get<PaginatedResponse<Concert>>('/concerts', { params, signal }),

  getById: (id: string) =>
    apiClient.get<Concert>(`/concerts/${id}`),

  create: (data: Partial<Concert>) =>
    apiClient.post<Concert>('/concerts', data),

  update: (id: string, data: Partial<Concert>) =>
    apiClient.patch<Concert>(`/concerts/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/concerts/${id}`),
};

// Concert Views endpoints
export const concertViewsAPI = {
  list: () =>
    apiClient.get<ConcertView[]>('/concert-views'),

  create: (data: ConcertViewCreate) =>
    apiClient.post<ConcertView>('/concert-views', data),

  update: (id: string, data: Partial<ConcertViewCreate>) =>
    apiClient.patch<ConcertView>(`/concert-views/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/concert-views/${id}`),

  reorder: (viewIds: string[]) =>
    apiClient.put('/concert-views/reorder', { view_ids: viewIds }),

  /** Overwrite the reserved "Latest" view, creating it on the first call. See docs/LATEST_VIEW.md. */
  saveLatest: (filters: ConcertViewFilters) =>
    apiClient.put<ConcertView>('/concert-views/latest', { filters }),
};

// Todos endpoints
export const todosAPI = {
  list: (
    params?: TodoSearchParams & { include_dismissed?: boolean; venue_id?: string },
    signal?: AbortSignal,
  ) => apiClient.get<PaginatedResponse<Reminder>>('/todos', { params, signal }),

  /** Unfiltered counts for the navbar badge and the todo page's filter dropdowns. */
  counts: () => apiClient.get<TodoCounts>('/todos/counts'),

  getById: (id: string) =>
    apiClient.get<Reminder>(`/todos/${id}`),

  create: (data: ReminderCreate) =>
    apiClient.post<Reminder>('/todos', data),

  update: (id: string, data: Partial<ReminderCreate & { status?: string }>) =>
    apiClient.patch<Reminder>(`/todos/${id}`, data),

  done: (id: string) =>
    apiClient.post<Reminder>(`/todos/${id}/done`),

  dismiss: (id: string) =>
    apiClient.post<Reminder>(`/todos/${id}/dismiss`),

  snooze: (id: string, days: number) =>
    apiClient.post<Reminder>(`/todos/${id}/snooze`, null, { params: { days } }),

  delete: (id: string) =>
    apiClient.delete(`/todos/${id}`),
};

// Email templates endpoints
export const emailTemplatesAPI = {
  list: (params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<EmailTemplate>>('/email-templates', { params }),

  getById: (id: string) =>
    apiClient.get<EmailTemplate>(`/email-templates/${id}`),

  create: (data: Partial<EmailTemplate>) =>
    apiClient.post<EmailTemplate>('/email-templates', data),

  update: (id: string, data: Partial<EmailTemplate>) =>
    apiClient.patch<EmailTemplate>(`/email-templates/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/email-templates/${id}`),
};

// Mailing Lists endpoints
export const mailingListsAPI = {
  list: (params?: { page?: number; page_size?: number }, signal?: AbortSignal) =>
    apiClient.get<PaginatedResponse<MailingList>>('/mailing-lists', { params, signal }),

  search: (q: string, signal?: AbortSignal) =>
    apiClient.get<PaginatedResponse<MailingList>>('/mailing-lists/search', { params: { q }, signal }),

  getById: (id: string) =>
    apiClient.get<MailingList>(`/mailing-lists/${id}`),

  create: (data: Partial<MailingList>) =>
    apiClient.post<MailingList>('/mailing-lists', data),

  update: (id: string, data: Partial<MailingList>) =>
    apiClient.patch<MailingList>(`/mailing-lists/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/mailing-lists/${id}`),

  // Campaigns (docs/CAMPAIGNS.md)
  listCampaigns: (mailingListId: string, params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<Campaign>>(`/mailing-lists/${mailingListId}/campaigns`, { params }),

  sendCampaign: (
    mailingListId: string,
    data: {
      subject: string;
      body: string;
      max_per_day?: number; // 1-400; sets the spacing (docs/EMAIL_SEND.md)
      send_period?: SendPeriod;
      create_todo?: boolean;
      todo_days?: number;
      /** Reach venues that asked not to be written to yet (docs/VENUE_PAUSE.md). */
      include_paused?: boolean;
      /** Only write where the venue has stayed quiet since the last campaign (docs/CAMPAIGNS.md). */
      only_quiet_venues?: boolean;
      /** Reply to the last mail each contact was sent, in its thread (docs/EMAIL_SEND.md). */
      reply_to_latest?: boolean;
    }
    // Queued in the background: poll the task, whose result is a CampaignSendResponse
  ) => apiClient.post<TaskStartedResponse>(`/mailing-lists/${mailingListId}/campaigns`, data),

  /** Per member: whether its venue answered each campaign of the list (docs/CAMPAIGNS.md). */
  memberAnswers: (mailingListId: string, signal?: AbortSignal) =>
    apiClient.get<MemberAnswers>(`/mailing-lists/${mailingListId}/member-answers`, { signal }),

  /** What a send would reach right now, and who it would leave out and why. Queues nothing. */
  /**
   * Who a send would reach. `replyToLatest` additionally asks for `repliable` — how many of those
   * recipients have a mail to reply to — which is a read of every recipient's communications, so
   * it is only passed while the reply box is ticked (docs/EMAIL_SEND.md).
   */
  recipientsPreview: (
    mailingListId: string,
    includePaused = false,
    onlyQuietVenues = false,
    replyToLatest = false,
    signal?: AbortSignal
  ) =>
    apiClient.get<RecipientsPreview>(`/mailing-lists/${mailingListId}/recipients-preview`, {
      params: {
        include_paused: includePaused,
        only_quiet_venues: onlyQuietVenues,
        reply_to_latest: replyToLatest,
      },
      signal,
    }),
};

export interface BatchRecipient {
  contact_id: string;
  to: string;
}

export interface SendPeriod {
  days_of_week: number[]; // 0=Mon...6=Sun; empty = all days
  hours: number[];        // every allowed hour (0-23) — gaps are honoured
  hour_start: number;     // 0-23 inclusive — derived from `hours`, kept for older clients
  hour_end: number;       // 1-24 exclusive — derived from `hours`, kept for older clients
  timezone: string;       // IANA timezone name
}

export interface SendBatchEmailResponse {
  success: boolean;
  batch_id: string;
  scheduled_count: number;
  skipped_count: number;
  first_scheduled_at: string;
  last_scheduled_at: string;
  days_spanned: number;          // distinct days the layout covers
  deferred_by_cap_count: number; // recipients pushed past the first 24h by the per-day limits
}

/** Where a batch would land if queued now: the same layout as a real send, nothing written. */
export interface SchedulePreviewResponse {
  first_scheduled_at: string;
  last_scheduled_at: string;
  days_spanned: number;
  deferred_by_cap_count: number;
  interval_seconds: number; // the spacing max_per_day works out to inside the send window
}

/**
 * What a send answers with. A scheduled send reports the queued email instead and carries none of
 * the sent-mail fields. `favorited_band_ids` are the bands the mail named, which the server has
 * just added to the favorites of the contact's venues — a queued one favorites them at dispatch,
 * so it reports none here.
 */
export interface SendEmailResponse {
  success: boolean;
  scheduled?: boolean;
  scheduled_email_id?: string;
  scheduled_at?: string;
  message_id?: string;
  thread_id?: string;
  todo_created?: boolean;
  favorited_band_ids?: string[];
}

// Gmail endpoints
export const emailImagesAPI = {
  list: () => apiClient.get<EmailImage[]>('/email-images'),

  upload: (file: File, width: number, height: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('width', String(width));
    formData.append('height', String(height));
    return apiClient.post<EmailImage>('/email-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** The bytes. Fetched through axios rather than an `<img src>` because the access token is an
   *  Authorization header, which an `<img>` tag does not send (docs/EMAIL_IMAGES.md). */
  getBytes: (id: string) => apiClient.get<Blob>(`/email-images/${id}`, { responseType: 'blob' }),

  usage: (id: string) => apiClient.get<EmailImageUsage>(`/email-images/${id}/usage`),

  remove: (id: string) =>
    apiClient.delete<{ success: boolean; usage: EmailImageUsage }>(`/email-images/${id}`),
};

export const gmailAPI = {
  send: (data: {
    contact_id: string;
    to: string;
    subject: string;
    body: string;
    create_todo?: boolean;
    todo_days?: number;
    thread_id?: string;
    in_reply_to?: string;
    venue_id?: string;
    band_ids?: string[];
    scheduled_at?: string;
  }) => apiClient.post<SendEmailResponse>('/gmail/send', data),

  sendBatch: (data: {
    recipients: BatchRecipient[];
    subject: string;
    body: string;
    max_per_day?: number;
    send_period?: SendPeriod;
    create_todo?: boolean;
    todo_days?: number;
    /** Reach venues that asked not to be written to yet (docs/VENUE_PAUSE.md). */
    include_paused?: boolean;
    /** Reply to the last mail each contact was sent, in its thread (docs/EMAIL_SEND.md). */
    reply_to_latest?: boolean;
    venue_id?: string;
    band_ids?: string[];
  }) => apiClient.post<SendBatchEmailResponse>('/gmail/send-batch', data),

  schedulePreview: (data: { count: number; max_per_day: number; send_period?: SendPeriod }) =>
    apiClient.post<SchedulePreviewResponse>('/gmail/schedule-preview', data),

  sync: () => apiClient.post<{ task_id: string }>('/gmail/sync'),

  rescheduleScheduled: (scheduledId: string, scheduledAt: string) =>
    apiClient.patch(`/gmail/scheduled/${scheduledId}`, { scheduled_at: scheduledAt }),

  cancelScheduled: (scheduledId: string) =>
    apiClient.delete(`/gmail/scheduled/${scheduledId}`),

  sendNowScheduled: (scheduledId: string) =>
    apiClient.post(`/gmail/scheduled/${scheduledId}/send-now`),

  /**
   * Put failed emails back in the queue, laid out under the normal pacing rules (send window,
   * spacing, daily max). Not "all at `startAt`" — that would hand the dispatcher N emails due in
   * the same tick and fire them as one burst. Omitting `startAt` means "as soon as allowed".
   */
  batchRequeueScheduled: (ids: string[], startAt?: string) =>
    apiClient.post<BatchRequeueResponse>('/gmail/scheduled/batch-requeue', {
      ids,
      ...(startAt ? { start_at: startAt } : {}),
    }),

  /** Cancel a selection of pending or failed emails and drop their placeholders. */
  batchCancelScheduled: (ids: string[]) =>
    apiClient.post<BatchCancelResponse>('/gmail/scheduled/batch-cancel', { ids }),

  /** Send a small selection right now, bypassing its pacing. Capped server-side. */
  batchSendNowScheduled: (ids: string[]) =>
    apiClient.post<BatchSendNowResponse>('/gmail/scheduled/batch-send-now', { ids }),
};

// Background Tasks endpoints
export const tasksAPI = {
  get: (taskId: string) =>
    apiClient.get<BackgroundTask>(`/tasks/${taskId}`),

  list: () =>
    apiClient.get<Array<{
      id: string;
      type: string;
      status: string;
      progress: number;
      message: string;
      result: Record<string, unknown> | null;
      error: string | null;
      created_at: string;
      updated_at: string;
    }>>('/tasks'),
};

// CSV Import endpoints
export const csvImportAPI = {
  analyze: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<CSVAnalyzeResponse>('/import/csv/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  validate: (data: CSVValidateRequest) =>
    apiClient.post<CSVValidateResponse>('/import/csv/validate', data),

  import: (data: CSVImportRequest) =>
    apiClient.post<{ task_id: string }>('/import/csv/import', data),

  history: () =>
    apiClient.get<ImportRecord[]>('/import/history'),

  deleteImport: (importId: string) =>
    apiClient.delete<{ venues_deleted: number; contacts_deleted: number }>(`/import/${importId}`),
};

// Data Corrections endpoints
export const dataCorrectionsAPI = {
  create: (data: DataCorrectionCreate) =>
    apiClient.post<DataCorrection>('/data-corrections', data),

  adminListAll: () =>
    apiClient.get<PaginatedResponse<DataCorrection>>('/data-corrections/admin/all'),

  adminUpdateStatus: (correctionId: string, status: DataCorrection['status']) =>
    apiClient.patch<DataCorrection>(`/data-corrections/${correctionId}/status`, { status }),
};

// Admin endpoints
export const adminAPI = {
  getStats: () => apiClient.get<AdminStats>('/admin/stats'),

  getUsers: (params?: { skip?: number; limit?: number }) =>
    apiClient.get<AdminUserList>('/admin/users', { params }),

  updateUser: (userId: string, data: { tier?: string; is_admin?: boolean }) =>
    apiClient.patch<AdminUser>(`/admin/users/${userId}`, data),

  deleteUser: (userId: string) =>
    apiClient.delete(`/admin/users/${userId}`),

  revokeGmail: (userId: string) =>
    apiClient.post<AdminUser>(`/admin/users/${userId}/revoke-gmail`),

  getCoreVenues: () =>
    apiClient.get<AdminCoreVenueList>('/admin/core-venues'),

  getCoreVenue: (venueId: string) =>
    apiClient.get<AdminCoreVenue>(`/admin/core-venues/${venueId}`),

  updateCoreVenue: (venueId: string, data: AdminCoreVenueUpdate) =>
    apiClient.patch<AdminCoreVenue>(`/admin/core-venues/${venueId}`, data),

  mergeCoreVenues: (targetId: string, sourceIds: string[]) =>
    apiClient.post<MergeCoreVenueResponse>('/admin/core-venues/merge', {
      target_id: targetId,
      source_ids: sourceIds,
    }),

  emergencyRevoke: () =>
    apiClient.post<EmergencyRevokeResponse>('/admin/emergency-revoke'),
};

export const analyticsAPI = {
  getDashboard: () =>
    apiClient.get<DashboardAnalytics>('/analytics/dashboard'),
};
